import React, { useMemo, useState } from 'react';
import { ChevronsUpDown, Loader2, Replace, Trash2 } from 'lucide-react';
import { Text } from '@cotera/client/app/components/ui';
import { Button } from '@cotera/client/v0/components/ui/button';
import {
  Command,
  CommandEmpty,
  CommandInput,
  CommandItem,
  CommandList,
} from '@cotera/client/v0/components/ui/command';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@cotera/client/v0/components/ui/popover';
import { cn } from '@cotera/client/v0/lib/utils';
import type { WorkflowOption } from '../trigger/hooks';
import { readNestedWorkflowReference } from './nested-workflow-tree';
import type { NestedWorkflowCardNodeData } from './nested-workflow-tree-to-flow';
import { useNestedTreeEditing } from './nested-workflow-tree-context';

/**
 * Classes every clickable thing inside a tree card or box header must carry.
 *
 * `pointer-events-auto` is load-bearing, not decoration. React Flow puts
 * `pointer-events: none` on a node wrapper unless the node is selectable,
 * draggable, or the canvas has an `onNodeClick` (see `NodeWrapper` in
 * @xyflow/react) — and this canvas is deliberately none of those. Without this,
 * every control in the tree renders and silently does nothing.
 *
 * Re-enabling it per control rather than per node is what keeps drag-to-pan
 * working across the whole surface: the boxes cover most of the canvas, so
 * giving *them* pointer events would leave nowhere to grab.
 *
 * `nodrag`/`nopan` then stop a click on the control from panning underneath it.
 */
export const NESTED_TREE_CONTROL_CLASS = 'pointer-events-auto nodrag nopan';

/** Copy for what happens when the edit lands, which differs per write path. */
function commitNote(drafted: boolean): string {
  return drafted ? 'Saved with your other changes.' : 'Saves immediately.';
}

/**
 * Picks the workflow a step should run.
 *
 * Local rather than the shared `Combobox` for two reasons, both about workflow
 * names being long: that one is a fixed 200px with no truncation, so a real name
 * overflows its button, and it feeds the row's *id* to the command list as the
 * filter text — so typing a name searched against UUIDs and matched nothing.
 * Here the trigger truncates, the list matches the width of whatever it opened
 * from, and rows filter on the name the user can actually see.
 */
const WorkflowTargetPicker: React.FC<{
  workflows: readonly WorkflowOption[];
  /** Never offered as a target — a workflow cannot run itself. */
  excludeWorkflowId: string;
  selectedWorkflowId?: string;
  placeholder: string;
  onPick: (workflow: { id: string; name: string }) => void;
}> = ({
  workflows,
  excludeWorkflowId,
  selectedWorkflowId,
  placeholder,
  onPick,
}) => {
  const [open, setOpen] = useState(false);
  const options = useMemo(
    () => workflows.filter((workflow) => workflow.id !== excludeWorkflowId),
    [excludeWorkflowId, workflows]
  );
  const selected = options.find(
    (workflow) => workflow.id === selectedWorkflowId
  );

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        {/* A plain button, deliberately: Radix's trigger already supplies the
            correct `aria-haspopup`/`aria-controls`/`aria-expanded` for what this
            opens, and claiming `role="combobox"` without the rest of that
            pattern (a listbox it controls, active-descendant tracking) describes
            the control less accurately than the button it is. Its name comes
            from its own text — the current target, or the placeholder. */}
        <button
          type="button"
          className="border-input bg-background hover:bg-muted/60 flex h-9 w-full min-w-0 items-center justify-between gap-2 rounded-md border px-3 text-left text-sm"
        >
          <span
            className={cn(
              'min-w-0 flex-1 truncate',
              selected === undefined && 'text-muted-foreground'
            )}
          >
            {selected?.name ?? placeholder}
          </span>
          <ChevronsUpDown className="h-4 w-4 shrink-0 opacity-50" />
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        className="nowheel w-(--radix-popover-trigger-width) p-0"
      >
        <Command>
          <CommandInput placeholder="Search workflows…" />
          <CommandList>
            <CommandEmpty>No other workflows.</CommandEmpty>
            {options.map((workflow) => (
              <CommandItem
                key={workflow.id}
                // The name, not the id: this is what the list filters on.
                value={workflow.name}
                onSelect={() => {
                  setOpen(false);
                  onPick({ id: workflow.id, name: workflow.name });
                }}
              >
                <span className="min-w-0 flex-1 truncate">{workflow.name}</span>
              </CommandItem>
            ))}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
};

/** The frame each control shape draws; see {@link NestedTreeControlButton}. */
const CONTROL_SHAPES = {
  /** On a card's corner: a bordered circle, as the editor canvas draws its own. */
  corner:
    'border-border bg-background text-muted-foreground hover:text-foreground hover:border-foreground/30 h-6 w-6 rounded-full border shadow-sm',
  /** In a group's title bar: borderless, matching the header's other controls. */
  header:
    'text-muted-foreground hover:bg-muted hover:text-foreground h-7 w-7 shrink-0 rounded-md',
} as const;

/** Spinner sized to match the icon each shape carries. */
const CONTROL_SPINNER_CLASS = {
  corner: 'h-3 w-3',
  header: 'h-3.5 w-3.5',
} as const;

/**
 * A clickable control on a tree card or box header.
 *
 * One component for both shapes the tree uses — the round badge on a card's
 * corner and the square button in a group's title bar — because everything that
 * makes a control *work* here is the same either way: the pointer-events class
 * above, `stopPropagation` so a click never reaches the canvas, and swapping in a
 * spinner while an edit is in flight. Only the frame differs, so only the frame
 * is a prop.
 */
export const NestedTreeControlButton = React.forwardRef<
  HTMLButtonElement,
  {
    title: string;
    shape: keyof typeof CONTROL_SHAPES;
    busy?: boolean;
    children: React.ReactNode;
  } & React.ComponentPropsWithoutRef<'button'>
>(
  (
    { title, shape, busy = false, children, className, onClick, ...rest },
    ref
  ) => (
    <button
      ref={ref}
      type="button"
      title={title}
      aria-label={title}
      disabled={busy}
      className={cn(
        NESTED_TREE_CONTROL_CLASS,
        'flex items-center justify-center transition-colors disabled:opacity-60',
        CONTROL_SHAPES[shape],
        className
      )}
      // Toggling is Radix's job (this is a PopoverTrigger child) — setting the
      // open state here too raced its toggle and left the popover stuck open.
      onClick={(event) => {
        event.stopPropagation();
        onClick?.(event);
      }}
      {...rest}
    >
      {busy ? (
        <Loader2 className={cn(CONTROL_SPINNER_CLASS[shape], 'animate-spin')} />
      ) : (
        children
      )}
    </button>
  )
);
NestedTreeControlButton.displayName = 'NestedTreeControlButton';

/**
 * Re-point or remove one `run-workflow` step, from its card in the tree.
 *
 * Only nesting is editable here: which workflow this step runs, and whether the
 * step stays. Anything else about the node is edited in the real editor, which
 * the group header's "open" button is one click from.
 */
export const NestedWorkflowCardAffordance: React.FC<{
  data: NestedWorkflowCardNodeData;
}> = ({ data }) => {
  const editing = useNestedTreeEditing();
  const [open, setOpen] = useState(false);
  const [confirmingRemove, setConfirmingRemove] = useState(false);

  if (editing === null) {
    return null;
  }
  // Nesting edits only — every other node type keeps the read-only card it had.
  const currentTarget = readNestedWorkflowReference(data.node);
  if (currentTarget === undefined) {
    return null;
  }

  const { editor, workflows } = editing;
  const { ownerWorkflowId, ownerNodeId, removalImpact } = data;
  const drafted = editor.editsAreDrafted(ownerWorkflowId);
  const busy = editor.pending.has(ownerWorkflowId);

  const close = (): void => {
    setOpen(false);
    setConfirmingRemove(false);
  };

  const disconnects =
    removalImpact.upstreamCount > 0 && removalImpact.downstreamCount > 0;

  return (
    <Popover
      open={open}
      onOpenChange={(next) => {
        setOpen(next);
        if (!next) {
          setConfirmingRemove(false);
        }
      }}
    >
      <PopoverTrigger asChild>
        <NestedTreeControlButton
          title="Change which workflow this step runs"
          shape="corner"
          busy={busy}
        >
          <Replace className="h-3 w-3" />
        </NestedTreeControlButton>
      </PopoverTrigger>
      <PopoverContent
        align="end"
        side="right"
        className="nowheel nopan nodrag w-72 p-3"
      >
        {confirmingRemove ? (
          <div className="flex flex-col gap-2">
            <Text.Caption className="text-foreground font-medium">
              Remove this step?
            </Text.Caption>
            <Text.Caption className="text-muted-foreground">
              {disconnects
                ? `The ${
                    removalImpact.upstreamCount === 1
                      ? 'step'
                      : `${removalImpact.upstreamCount} steps`
                  } before it and the ${
                    removalImpact.downstreamCount === 1
                      ? 'step'
                      : `${removalImpact.downstreamCount} steps`
                  } after it will be left unconnected.`
                : 'The workflow it runs is not deleted — only this step.'}
            </Text.Caption>
            <div className="flex justify-end gap-2 pt-1">
              <Button
                variant="outline"
                size="sm"
                className="h-7"
                onClick={() => setConfirmingRemove(false)}
              >
                Cancel
              </Button>
              <Button
                variant="destructive"
                size="sm"
                className="h-7"
                onClick={() => {
                  close();
                  void editor.applyEdit(ownerWorkflowId, {
                    kind: 'remove',
                    nodeId: ownerNodeId,
                  });
                }}
              >
                Remove
              </Button>
            </div>
          </div>
        ) : (
          <div className="flex flex-col gap-2">
            <Text.Caption className="text-muted-foreground">
              Runs workflow
            </Text.Caption>
            <WorkflowTargetPicker
              workflows={workflows}
              excludeWorkflowId={ownerWorkflowId}
              selectedWorkflowId={currentTarget}
              placeholder="Pick a workflow"
              onPick={(picked) => {
                close();
                if (picked.id === currentTarget) {
                  return;
                }
                void editor.applyEdit(ownerWorkflowId, {
                  kind: 'repoint',
                  nodeId: ownerNodeId,
                  targetWorkflowId: picked.id,
                  targetWorkflowName: picked.name,
                });
              }}
            />
            <Text.Caption className="text-muted-foreground/80 text-[11px]">
              {commitNote(drafted)} Mapped inputs are cleared.
            </Text.Caption>
            <div className="border-t pt-2">
              <Button
                variant="ghost"
                size="sm"
                className="text-destructive hover:text-destructive hover:bg-destructive/10 h-7 w-full justify-start gap-2 px-2"
                onClick={() => setConfirmingRemove(true)}
              >
                <Trash2 className="h-3.5 w-3.5" />
                Remove this step
              </Button>
            </div>
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
};
